import { useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import Button from "components/Button";

import { useAuthStore } from "@type/store";
import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";
import { logError } from "lib/sentry/logError";

interface FallbackProps {
  error?: any;
  customError?: any;
  size?: "lg" | "md";
  fullPage?: boolean;
  fullLayout?: boolean;
  withImage?: boolean;
}

const Fallback = ({
  error,
  customError,
  size = "lg",
  fullPage = false,
  fullLayout = false,
  withImage,
}: FallbackProps) => {
  const user = useAuthStore((state) => state.user);
  const requestedRef = useRef(false);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoToHomepage = () => {
    window.location.href = "/";
  };

  const uuid = useMemo(() => uuidv4(), []);

  const parsedRefId = useMemo(() => {
    if (!error?.data) return "";

    let objError = error?.data;
    if (typeof error?.data === "string") {
      try {
        objError = JSON.parse(error?.data);
      } catch {
        objError = undefined;
      }
    }

    return objError?.ref_id?.toUpperCase()?.replace(/[()]/g, "");
  }, [error]);

  useEffect(() => {
    if ((error || customError) && !requestedRef.current) {
      const usedError = customError || error;

      logError({
        traceId: uuid,
        user: user,
        error: usedError,
      });

      requestedRef.current = true;
    }
  }, [error, customError, uuid, user]);

  if (fullPage || fullLayout) {
    return (
      <div
        className={cn(
          "w-full flex items-center justify-center px-6",
          fullPage ? "h-[100vh]" : "h-[calc(100vh-13rem)]"
        )}
      >
        <div className="w-fit">
          <FallbackComponent
            traceId={uuid}
            refId={parsedRefId}
            size={size}
            withImage={withImage}
            onRefresh={handleRefresh}
            onGoToHomepage={handleGoToHomepage}
          />
        </div>
      </div>
    );
  }

  return (
    <FallbackComponent
      traceId={uuid}
      refId={parsedRefId}
      size={size}
      onRefresh={handleRefresh}
      onGoToHomepage={handleGoToHomepage}
    />
  );
};

export default Fallback;

interface FallbackComponentProps {
  hideAction?: boolean;
  traceId: string;
  refId?: string;
  size?: "lg" | "md";
  customMessage?: string;
  withImage?: boolean;
  onRefresh?: () => void;
  onGoToHomepage?: () => void;
}

export const FallbackComponent = ({
  hideAction = false,
  traceId,
  refId,
  size,
  customMessage,
  withImage,
  onRefresh,
  onGoToHomepage,
}: FallbackComponentProps) => {
  const { t } = useTranslationContext();
  return (
    <div className="w-full" role="alert">
      {withImage && (
        <div className="flex items-center justify-center">
          <img
            width={201}
            height={237}
            className="max-w-[12.5rem] max-h-[14.8rem] mb-11"
            src="/images/hs-maskot.webp"
            alt="Error"
          />
        </div>
      )}

      <div
        className={cn(
          "text-night font-semibold",
          size === "lg" ? "text-2xl mb-3" : "text-xl mb-2",
          withImage ? "text-center" : "text-left"
        )}
      >
        {customMessage || t("global_error.message")}
      </div>

      <div className={cn(size === "lg" ? "mb-9" : "mb-5")}>
        <div
          className={cn(
            "text-base text-night mb-2",
            withImage ? "text-center" : "text-left"
          )}
        >
          {t("global_error.message.info")}
        </div>

        <div className="w-full text-base text-night bg-gray-100 rounded-md py-3 px-3">
          <div className="flex flex-col gap-1">
            {refId && (
              <div className="flex gap-2">
                <div className="font-semibold">
                  {t("global_error.message.refference_id")}
                </div>
                <div>{refId?.replace(/[()]/g, "")}</div>
              </div>
            )}
            <div className={refId ? "flex gap-2" : ""}>
              <div className="font-semibold">
                {t("global_error.message.trace_id")}
              </div>
              <div>{traceId}</div>
            </div>
          </div>
        </div>
      </div>

      {!hideAction && (
        <div
          className={cn(
            "flex items-center gap-4",
            withImage ? "justify-center" : "justify-start"
          )}
        >
          <Button className="text-base" onClick={onGoToHomepage}>
            {t("global_error.message.action.go_to_homepage")}
          </Button>

          <Button
            className="min-w-[5.5rem] text-base"
            variant="outlined"
            onClick={onRefresh}
          >
            {t("global_error.message.action.refresh")}
          </Button>
        </div>
      )}
    </div>
  );
};
